// COTELE PLANȘEI DE SITUAȚIE — distanțele pe care le verifică cineva care
// citește o așezare, nu toate distanțele care se pot măsura.
//
// Planul avea deja cote pe CONTUR: fiecare colț numerotat, fiecare latură cu
// lungimea ei (`siteDerive.boundaryMarks`). Ce lipsea e chiar ce se verifică
// întâi pe o așezare — cât e între corpuri și cât rămâne până la limita de
// proprietate. Sunt și cele două mărimi pe care regimul urbanistic le
// dictează, iar de când se pot scrie în ecran («Retrageri», 15.09.2026) trebuie
// să se poată și citi pe desen: altfel omul scrie 16,50 și n-are unde vedea
// dacă asta a ieșit.
//
// CE NU SE COTEAZĂ, ȘI DE CE. Un plan cu toate distanțele pe el nu se citește.
// Lățimea aleii și adâncimea locului sunt constante ale proiectului (6,00 și
// 5,00 m), iar pasul locurilor are deja lanțul lui la zoom mare
// (`siteDerive.runChain`). Ce rămâne de măsurat sunt distanțele pe care le
// alege ASEZAREA, nu cele pe care le alege catalogul.
//
// PARCAJUL se cotează totuși: nu lățimea lui, ci cât stă de fațadă. E singura
// distanță dintre un corp și mobilierul curții care se verifică pe planșă
// (5,00 m de la locuri la fațadă), iar fără ea locurile desenate lângă bloc nu
// se pot contesta. Una per RÂND de locuri (Oana, 28.09.2026: «distanța între
// fiecare alee cu fâșie de mașini și fațada apropiată ei»), de la marginea
// rândului până la fațada din fața lui, pe mijlocul porțiunii pe care se
// privesc — nu una per corp, care pe un corp cu două rânduri în față n-o
// arăta decât pe cea mai apropiată.
//
// ȘI NU TOATE PERECHILE. Nouă corpuri fac 36 de perechi, dintre care cele mai
// multe trec peste alte blocuri și nu spun nimic. Se cotează perechile de
// VECINI, iar „vecin" e o relație geometrică, nu un clasament: doi corpi se
// cotează dacă se PRIVESC — fațadele lor se suprapun pe o lungime care merită
// măsurată — și dacă între ei nu stă un al treilea corp.
//
// Regula dinainte alegea, pentru fiecare corp, doar cel mai apropiat vecin.
// Pe planul beneficiarului asta a ascuns exact rosturile care se verifică:
// corpurile vin în perechi la 4,00 m, așa că legăturile de 8,72 și 15,96 m
// dintre perechi — cele care se compară cu H/2 — au căzut toate. 5 cote în loc
// de 8. Un corp poate avea mai mulți vecini; câți are, atâtea rosturi are de
// arătat.

import { metri, type DimSpan } from "./siteDerive";
import type { Pt } from "../types";

/** Sub atât, o cotă nu spune nimic: două corpuri lipite sunt lipite, iar
 *  eticheta ar sta oricum peste desen. */
const MIN_COTA_M = 1.0;

/** Cât trebuie să se suprapună două fațade ca rostul dintre ele să fie o
 *  distanță, nu o atingere de colț. Sub atât, cei doi corpi stau alături fără
 *  să se privească, iar cota ar măsura un colț, nu un rost. */
const MIN_FATA_M = 2.0;

/** Peste atât, un loc de parcare sau o alee nu mai sunt „ale blocului": cota ar
 *  traversa curtea și n-ar mai spune nimic despre corpul de lângă care pleacă. */
const MAX_MOBILIER_M = 25.0;

/** Cât se scurtează legătura la capete când se caută cine stă între cei doi:
 *  capetele ei ating chiar corpurile cotate. */
const EPS_M = 0.05;

type Segment = { a: Pt; b: Pt; d: number };

type Umbra = { lo: number; hi: number };

type Directie = { ux: number; uy: number };

/** Relația dintre doi corpi care se privesc: pe ce direcție, cu ce gol între
 *  ei și pe ce lungime de fațadă. */
type Fata = {
  ux: number; uy: number; nx: number; ny: number;
  gol: Umbra; fata: Umbra;
};

/** Punctul de pe segmentul `[p, q]` cel mai apropiat de `c`. */
function peSegment(c: Pt, p: Pt, q: Pt): Pt {
  const vx = q[0] - p[0], vy = q[1] - p[1];
  const len2 = vx * vx + vy * vy;
  if (len2 < 1e-12) return p;
  let t = ((c[0] - p[0]) * vx + (c[1] - p[1]) * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  return [p[0] + t * vx, p[1] + t * vy];
}

/** Cea mai scurtă legătură între două inele, ca pereche de puncte.
 *
 *  Se compară fiecare vârf al unuia cu fiecare latură a celuilalt, în ambele
 *  sensuri. E O(n·m) — dar inelele de aici au patru până la douăsprezece
 *  vârfuri, deci e mai ieftin decât orice structură care ar accelera-o, și se
 *  poate citi fără să crezi pe cuvânt.
 *
 *  Nu verifică intersecția: două corpuri care s-ar suprapune sunt o eroare de
 *  așezare, iar cota lor ar ieși 0 și ar cădea sub `MIN_COTA_M`. */
function celMaiScurt(A: number[][], B: number[][]): Segment | null {
  if (A.length < 2 || B.length < 2) return null;
  let best: Segment | null = null;
  const incearca = (c: Pt, p: Pt, q: Pt, inversat: boolean) => {
    const m = peSegment(c, p, q);
    const d = Math.hypot(m[0] - c[0], m[1] - c[1]);
    if (best && d >= best.d) return;
    best = inversat ? { a: m, b: c, d } : { a: c, b: m, d };
  };
  for (const v of A) {
    for (let i = 0; i < B.length; i++) {
      incearca(v as Pt, B[i] as Pt, B[(i + 1) % B.length] as Pt, false);
    }
  }
  for (const v of B) {
    for (let i = 0; i < A.length; i++) {
      incearca(v as Pt, A[i] as Pt, A[(i + 1) % A.length] as Pt, true);
    }
  }
  return best;
}

/** Cât ocupă un inel pe o direcție dată — umbra lui pe o axă. */
function umbra(R: number[][], ux: number, uy: number): Umbra {
  let lo = Infinity, hi = -Infinity;
  for (const p of R) {
    const t = p[0] * ux + p[1] * uy;
    if (t < lo) lo = t;
    if (t > hi) hi = t;
  }
  return { lo, hi };
}

/** Cât au în comun două umbre; negativ dacă nu se ating. */
function comun(a: Umbra, b: Umbra): number {
  return Math.min(a.hi, b.hi) - Math.max(a.lo, b.lo);
}

/** Direcția pe care doi corpi se despart, cu golul dintre ei și fațada pe care
 *  se privesc — sau `null` dacă nu se privesc deloc.
 *
 *  Se încearcă normala fiecărei laturi, a amândurora: pe una dintre ele umbrele
 *  celor doi sunt separate, iar aceea e direcția rostului. Se ține cea cu
 *  despărțirea cea mai mare, fiindcă pentru corpuri convexe ea e chiar distanța
 *  dintre ei.
 *
 *  Asta e și deosebirea dintre un rost și o atingere din colț: două corpuri
 *  puse pe diagonală sunt separate pe amândouă axele, dar pe fiecare dintre ele
 *  umbrele lor NU se suprapun — nu au fațadă comună, deci n-au ce cota. */
function fataInFata(A: number[][], B: number[][]): Fata | null {
  let best: Fata | null = null;
  let celMaiMare = 0;
  for (const R of [A, B]) {
    for (let i = 0; i < R.length; i++) {
      const p = R[i], q = R[(i + 1) % R.length];
      const ex = q[0] - p[0], ey = q[1] - p[1];
      const len = Math.hypot(ex, ey);
      if (len < 1e-9) continue;
      const ux = ey / len, uy = -ex / len;
      const a = umbra(A, ux, uy), b = umbra(B, ux, uy);
      const rost = Math.max(b.lo - a.hi, a.lo - b.hi);
      if (rost <= 0 || rost <= celMaiMare) continue;
      const nx = -uy, ny = ux;
      const fa = umbra(A, nx, ny), fb = umbra(B, nx, ny);
      if (comun(fa, fb) < MIN_FATA_M) continue;
      celMaiMare = rost;
      best = {
        ux, uy, nx, ny,
        gol: { lo: Math.min(a.hi, b.hi), hi: Math.max(a.lo, b.lo) },
        fata: { lo: Math.max(fa.lo, fb.lo), hi: Math.min(fa.hi, fb.hi) },
      };
    }
  }
  return best;
}

/** Normalele fațadelor unui corp, fiecare o singură dată, îndreptate în afară.
 *
 *  Se pleacă din LATURI, nu din axele planșei: o bară rotită cu patru grade are
 *  fațadele ei, iar o retragere măsurată pe axele desenului ar fi oblică față
 *  de clădirea pe care o descrie. */
function fatade(corp: number[][]): Directie[] {
  const cx = corp.reduce((t, p) => t + p[0], 0) / corp.length;
  const cy = corp.reduce((t, p) => t + p[1], 0) / corp.length;
  const out: Directie[] = [];
  for (let i = 0; i < corp.length; i++) {
    const p = corp[i], q = corp[(i + 1) % corp.length];
    const ex = q[0] - p[0], ey = q[1] - p[1];
    const len = Math.hypot(ex, ey);
    if (len < 1e-9) continue;
    let ux = ey / len, uy = -ex / len;
    const mx = (p[0] + q[0]) / 2, my = (p[1] + q[1]) / 2;
    if (mx * ux + my * uy < cx * ux + cy * uy) { ux = -ux; uy = -uy; }
    if (out.some((v) => v.ux * ux + v.uy * uy > 0.9999)) continue;
    out.push({ ux, uy });
  }
  return out;
}

/** Cât are un corp până la limită pe direcția unei fațade, și de unde până unde
 *  se scrie cifra.
 *
 *  O clădire nu are O retragere, are câte una de fiecare fațadă, iar regimul
 *  urbanistic le cere separat — spre stradă altfel decât spre vecini. Se ia
 *  BANDA fațadei (umbra corpului pe perpendiculara direcției) și se caută
 *  punctul limitei care intră cel mai adânc în ea: așa cota pleacă din fațadă
 *  și cade pe limita din fața ei, nu pe un colț de lot aflat în altă parte.
 *
 *  Dacă un alt corp stă în drum, nu se scrie nimic: acolo ce se măsoară e
 *  rostul dintre cele două corpuri, iar el are deja cota lui. */
function retragere(corp: number[][], inel: number[][], u: Directie,
                   altele: number[][][]): Segment | null {
  const { ux, uy } = u;
  const nx = -uy, ny = ux;
  const banda = umbra(corp, nx, ny);
  const fata = umbra(corp, ux, uy).hi;
  let qx = 0, qy = 0, minU = Infinity;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const na = a[0] * nx + a[1] * ny, nb = b[0] * nx + b[1] * ny;
    const dn = nb - na;
    let t0 = 0, t1 = 1;
    if (Math.abs(dn) < 1e-12) {
      if (na < banda.lo || na > banda.hi) continue;
    } else {
      const tA = (banda.lo - na) / dn, tB = (banda.hi - na) / dn;
      t0 = Math.max(0, Math.min(tA, tB));
      t1 = Math.min(1, Math.max(tA, tB));
      if (t0 > t1) continue;
    }
    // Pe o latură dreaptă umbra crește liniar, deci cel mai mic punct al
    // porțiunii tăiate e la unul din capetele ei.
    for (const t of [t0, t1]) {
      const x = a[0] + (b[0] - a[0]) * t, y = a[1] + (b[1] - a[1]) * t;
      const pu = x * ux + y * uy;
      if (pu <= fata || pu >= minU) continue;
      minU = pu; qx = x; qy = y;
    }
  }
  if (!Number.isFinite(minU)) return null;
  const d = minU - fata;
  if (d < MIN_COTA_M) return null;
  const gol = { lo: fata + EPS_M, hi: minU - EPS_M };
  for (const alt of altele) {
    if (comun(umbra(alt, ux, uy), gol) > 0
        && comun(umbra(alt, nx, ny), banda) >= MIN_FATA_M) return null;
  }
  return { a: [qx - ux * d, qy - uy * d], b: [qx, qy], d };
}

/** Cota dintre două inele care se privesc, perpendiculară pe fațada comună,
 *  la SFERTUL porțiunii pe care se privesc — de la marginea unuia la a
 *  celuilalt. La sfert, nu la mijloc: lungimea corpului își are cota tot pe
 *  mijlocul fațadei, la 2 m de ea, iar un rând de locuri stă de obicei
 *  centrat pe fațadă — cele două cifre cădeau una peste alta. */
function cotaFata(A: number[][], B: number[][]): Segment | null {
  const f = fataInFata(A, B);
  if (!f) return null;
  const d = f.gol.hi - f.gol.lo;
  if (d < MIN_COTA_M) return null;
  const mij = f.fata.lo + (f.fata.hi - f.fata.lo) * 0.25;
  const la = (u: number): Pt => [u * f.ux + mij * f.nx, u * f.uy + mij * f.ny];
  return { a: la(f.gol.lo), b: la(f.gol.hi), d };
}

/** Corpul din fața unui rând de locuri — cel mai apropiat dintre cele cu care
 *  rândul se privește, dacă e destul de aproape ca rândul să fie al lui. */
function fatadaRandului(rand: number[][], corpuri: number[][][]): Segment | null {
  let cel: Segment | null = null;
  for (const corp of corpuri) {
    const s = cotaFata(rand, corp);
    if (!s || s.d > MAX_MOBILIER_M) continue;
    if (cel && s.d >= cel.d) continue;
    cel = s;
  }
  return cel;
}

/** Piesa de curte cea mai apropiată din fața corpului — aleea. Una singură per
 *  corp: ce se verifică e cât stă de fațadă, iar a doua cifră n-ar mai spune
 *  altceva. */
function celMaiAproape(corp: number[][], piese: number[][][]): Segment | null {
  let cel: Segment | null = null;
  for (const piesa of piese) {
    const s = celMaiScurt(corp, piesa);
    if (!s || s.d < MIN_COTA_M || s.d > MAX_MOBILIER_M) continue;
    if (cel && s.d >= cel.d) continue;
    if (!fataInFata(corp, piesa)) continue;
    cel = s;
  }
  return cel;
}

function span(s: Segment): DimSpan {
  return { x1: s.a[0], y1: s.a[1], x2: s.b[0], y2: s.b[1], label: metri(s.d) };
}

/** Lungimea sau lățimea unui corp, cotată în afara lui, cu linii ajutătoare.
 *
 *  `pa`/`pb` sunt colțurile de pe fațadă, `a`/`b` capetele liniei de cotă,
 *  duse în afară pe normala fațadei — ca pe o planșă desenată: cifra stă
 *  lângă corp, nu peste eticheta lui și nici peste rostul spre vecin. */
export interface CotaCorp {
  pa: Pt; pb: Pt; a: Pt; b: Pt;
  fel: "lungime" | "latime";
  /** Metri, două zecimale, cu virgulă: «47,30». */
  m: string;
}

export interface CoteSit {
  /** Lungimea și lățimea fiecărui corp, câte una. */
  dimensiuni: CotaCorp[];
  /** Între corpuri, o cotă per pereche de vecini. */
  intreCorpuri: DimSpan[];
  /** De la fiecare corp până la limita de proprietate, câte una de fiecare
   *  fațadă care chiar ajunge la limită. */
  retrageri: DimSpan[];
  /** De la fiecare RÂND de locuri până la fațada din fața lui, pe mijlocul
   *  porțiunii pe care se privesc — dacă are una destul de aproape ca rândul
   *  să fie al ei. */
  parcaje: DimSpan[];
  /** La fel, până la aleea din fața lui. */
  drumuri: DimSpan[];
}

/** Cât stă linia de cotă a unui corp de fațada lui. Destul ca să nu se lipească
 *  de conturul gros al clădirii, puțin cât să nu ajungă în aleea din față. */
const DEPARTARE_M = 2.0;


/** Lungimea și lățimea unui corp, pe axele LUI.
 *
 *  Axele se iau din latura cea mai lungă, nu din planșă: un corp rotit își
 *  are lungimea pe fațada lui, iar una măsurată pe axele desenului ar fi
 *  diagonala unui dreptunghi înclinat. Pentru un corp în L sau în U e
 *  dreptunghiul care îl cuprinde — gabaritul, adică ce se verifică pe o
 *  așezare.
 *
 *  Fiecare cotă stă pe latura cu mai mult LOC până la corpul vecin: între
 *  două bare la 4 m una de alta, linia de cotă pusă în rost ar cădea peste
 *  cota rostului. */
function dimensiuniCorp(corp: number[][], altele: number[][][]): CotaCorp[] {
  let ux = 1, uy = 0, lung = 0;
  for (let i = 0; i < corp.length; i++) {
    const p = corp[i], q = corp[(i + 1) % corp.length];
    const l = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (l > lung + 1e-6) { lung = l; ux = (q[0] - p[0]) / l; uy = (q[1] - p[1]) / l; }
  }
  if (lung < 1e-6) return [];
  const nx = -uy, ny = ux;
  const U = umbra(corp, ux, uy), N = umbra(corp, nx, ny);
  const pe = (s: number, t: number): Pt => [s * ux + t * nx, s * uy + t * ny];

  /** Golul liber de pe o parte a corpului: până la primul vecin care stă în
   *  fața ei. `dir` +1 = spre capătul `hi` al axei `v`. */
  const loc = (vx: number, vy: number, wx: number, wy: number,
               banda: Umbra, dir: 1 | -1) => {
    const eu = umbra(corp, vx, vy);
    let gol = Infinity;
    for (const alt of altele) {
      if (comun(umbra(alt, wx, wy), banda) <= 0) continue;
      const au = umbra(alt, vx, vy);
      const g = dir > 0 ? au.lo - eu.hi : eu.lo - au.hi;
      if (g >= 0 && g < gol) gol = g;
    }
    return gol;
  };

  const out: CotaCorp[] = [];
  // LUNGIMEA: pe o fațadă lungă, deasupra sau dedesubt pe normala `n`.
  const L = U.hi - U.lo, W = N.hi - N.lo;
  {
    const sus = loc(nx, ny, ux, uy, U, 1) >= loc(nx, ny, ux, uy, U, -1);
    const t = sus ? N.hi : N.lo, d = sus ? DEPARTARE_M : -DEPARTARE_M;
    out.push({ pa: pe(U.lo, t), pb: pe(U.hi, t),
               a: pe(U.lo, t + d), b: pe(U.hi, t + d),
               fel: "lungime", m: metri(L) });
  }
  // LĂȚIMEA: pe un capăt, la stânga sau la dreapta pe axa `u`.
  {
    const dr = loc(ux, uy, nx, ny, N, 1) >= loc(ux, uy, nx, ny, N, -1);
    const s = dr ? U.hi : U.lo, d = dr ? DEPARTARE_M : -DEPARTARE_M;
    out.push({ pa: pe(s, N.lo), pb: pe(s, N.hi),
               a: pe(s + d, N.lo), b: pe(s + d, N.hi),
               fel: "latime", m: metri(W) });
  }
  return out;
}

/** Cotele unei așezări: între corpuri și până la limită.
 *
 *  `corpuri` sunt inelele construite la sol, `lot` e limita de proprietate.
 *  Amândouă în metri de desen, în cadrul în care se desenează — adică DUPĂ
 *  `canonical.northUp`, ca tot restul planșei.
 */
export function coteleSitului(
  corpuri: number[][][], lot: Pt[],
  randuri: number[][][] = [], alei: number[][][] = [],
): CoteSit {
  const n = corpuri.length;
  const dimensiuni: CotaCorp[] = corpuri.flatMap((c, i) =>
    dimensiuniCorp(c, corpuri.filter((_, k) => k !== i)));
  const intreCorpuri: DimSpan[] = [];
  const retrageri: DimSpan[] = [];
  const parcaje: DimSpan[] = [];
  const drumuri: DimSpan[] = [];
  if (n === 0) return { dimensiuni, intreCorpuri, retrageri, parcaje, drumuri };

  // --- între corpuri: perechile care se privesc, cu nimic între ele -------- //
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const f = fataInFata(corpuri[i], corpuri[j]);
      if (!f) continue;
      const s = celMaiScurt(corpuri[i], corpuri[j]);
      if (!s || s.d < MIN_COTA_M) continue;

      // Un al treilea corp taie legătura dacă intră în golul dintre cei doi și
      // o face pe lungimea fațadei pe care ei se privesc. Atunci cota ar
      // traversa un bloc, iar cei doi nu mai sunt vecini.
      const gol = { lo: f.gol.lo + EPS_M, hi: f.gol.hi - EPS_M };
      let blocat = false;
      for (let k = 0; k < n && !blocat; k++) {
        if (k === i || k === j) continue;
        blocat = comun(umbra(corpuri[k], f.ux, f.uy), gol) > 0
          && comun(umbra(corpuri[k], f.nx, f.ny), f.fata) >= MIN_FATA_M;
      }
      if (!blocat) intreCorpuri.push(span(s));
    }
  }

  // --- până la limită: câte una de fiecare fațadă ------------------------- //
  if (lot.length >= 2) {
    const inel = lot as unknown as number[][];
    for (let i = 0; i < n; i++) {
      const altele = corpuri.filter((_, k) => k !== i);
      for (const u of fatade(corpuri[i])) {
        const s = retragere(corpuri[i], inel, u, altele);
        if (s) retrageri.push(span(s));
      }
    }
  }

  // --- până la curte: rândul de locuri până la fațadă, aleea din fața corpului //
  for (const rand of randuri) {
    const s = fatadaRandului(rand, corpuri);
    if (s) parcaje.push(span(s));
  }
  for (const corp of corpuri) {
    const alee = celMaiAproape(corp, alei);
    if (alee) drumuri.push(span(alee));
  }

  return { dimensiuni, intreCorpuri, retrageri, parcaje, drumuri };
}
